// SCR-02 home (plan 8.4.2): 'me' starts with the CLI / API 2-split cards; 'team' and 'all' start with number tiles.
// The summary endpoint returns the blocks for the requested scope (blocks the viewer may not see are null);
// chart cards (daily, models, heat, flow) are separate endpoints so each card loads/fails on its own.
import { z } from 'zod';
import { GapReason, MissingReason } from '../enums.js';
import { AppliedFilter, CardFooter, Comparison, Count, Delta, EpochMs, Id, RecordRef, TeamRef, ValuedNumber, Ymd, filterQuery } from '../common.js';
import { GroupChart, HeatChart, SplitRatio, SummaryFacts, TimeChart } from '../charts.js';
import { ApiSplitCard, CliSplitCard, ConnectionAlert, MemberUsageRow, SmallTeamsRow, TeamCompareRow } from './shared.js';
import { TeamsCompareResponse } from './people.js';
import { RecentConversation } from './records.js';
import { AuditEntry, BulkViewAlert } from './admin-audit.js';
import { ApprovalRow } from './admin-roles.js';

export const HomeQuery = filterQuery({});

/** Variant chosen by the server from effective permissions (mockup home.js variant): admin · ops · audit · stats. */
export const HOME_LAYOUTS = ['me', 'team', 'all_admin', 'all_ops', 'all_audit', 'all_stats'] as const;

/**
 * Number tiles per layout (mockup home.js): team → active_people, prompts, tokens, cost_cli_estimate ·
 * all_admin → active_people, prompts_today, tokens, errors · all_stats → active_people, prompts_today, tokens,
 * cost_cli_estimate · all_audit → active_people, content_views, bulk_view_alerts (+ the integrity tile drawn from
 * `audit.integrity`) · all_ops → received_today, connections_ok, connections_attention, error_rate.
 */
export const KPI_KEYS = [
  'active_people',
  'prompts',
  'prompts_today',
  'tokens',
  'cost_cli_estimate',
  'cost_price_table',
  'errors',
  'error_rate',
  'received_today',
  'stored_today',
  'connections_ok',
  'connections_attention',
  'content_views',
  'bulk_view_alerts',
] as const;
/**
 * Numbers behind a tile's hint line: errors → errors · rejects; error_rate → failed · requests; cost_cli_estimate →
 * price_table (gateway price-table amount, not added); connections_attention → problem · down · quiet;
 * content_views → success · denied.
 */
export const KPI_PART_KEYS = ['errors', 'rejects', 'failed', 'requests', 'price_table', 'problem', 'down', 'quiet', 'success', 'denied'] as const;
export const KpiTile = z.object({
  key: z.enum(KPI_KEYS),
  unit: z.enum(['people', 'count', 'tokens', 'usd_micros', 'pct']),
  cur: ValuedNumber,
  /** null for tiles without a comparison (connections, views, bulk alerts). */
  prev: ValuedNumber.nullable(),
  delta: Delta.nullable(),
  /** `yesterday` = today so far vs yesterday up to the same time (prompts_today, received_today). */
  base: z.enum(['prev_period', 'yesterday']).nullable(),
  /** Population for ratios like "활성 12/40명", or all connections for connections_ok. */
  of: Count.nullable(),
  parts: z.partialRecord(z.enum(KPI_PART_KEYS), ValuedNumber).optional(),
  /** Small trend line values (daily or hourly), oldest first. */
  spark: z.array(z.number()).nullable(),
});
export type KpiTile = z.infer<typeof KpiTile>;

/**
 * "처리할 열람 일이 있나요?" (access.approve holders). `pendingApprovals` = the same rows as admin.approvals.list
 * that this viewer may decide (requester ≠ viewer); deciding needs a reason (plan 7.4.2), so the home card opens the
 * same reason dialog as the admin screen. `handled` = what this viewer decided lately.
 */
export const AccessWorkCard = z.object({
  pendingApprovals: z.array(ApprovalRow),
  handled: z.array(z.object({ id: Id, code: z.string(), outcome: z.enum(['approved', 'rejected']), decidedAt: EpochMs })).max(10),
  activeCases: z.array(z.object({ id: Id, code: z.string(), title: z.string(), to: Ymd, targets: Count, views: Count })),
  myGrants: z.array(z.object({ record: RecordRef, expiresAt: EpochMs, minutesLeft: z.int().nonnegative() })),
});

export const ANOMALY_KINDS = ['spike', 'down', 'idle', 'quiet', 'bulk_view', 'gap'] as const;
/** "이상 징후" (all scope). Ignores period/team filter; filtered by system.view / audit.view. Small teams never appear. */
export const Anomaly = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('spike'), team: TeamRef, ratio: z.number().positive(), todayTokens: Count, avgTokens: Count }),
  z.object({ kind: z.literal('down'), count: Count, items: z.array(ConnectionAlert) }),
  z.object({ kind: z.literal('idle'), count: Count }),
  z.object({ kind: z.literal('quiet'), count: Count }),
  BulkViewAlert.extend({ kind: z.literal('bulk_view') }),
  z.object({ kind: z.literal('gap'), from: EpochMs, to: EpochMs, minutes: Count, alerted: z.boolean(), ongoing: z.boolean(), reason: GapReason }),
]);
export type Anomaly = z.infer<typeof Anomaly>;

export const OrgReportChannel = z.object({
  provider: z.enum(['anthropic', 'openai']),
  status: z.enum(['ok', 'warn', 'error', 'not_connected']),
  lastSyncAt: EpochMs.nullable(),
  issue: z.enum(['late', 'auth', 'rate_limited', 'no_admin_key']).nullable(),
});
export const IngestSummary = z.object({
  received: Count,
  duplicates: Count,
  dropped: Count,
  lastReceivedAt: EpochMs.nullable(),
  connections: z.object({ ok: Count, quiet: Count, down: Count, problem: Count }),
  gaps7d: z.object({ count: Count, longestMinutes: Count }),
  orgReports: z.array(OrgReportChannel),
});

export const SETUP_STEPS = ['ceiling', 'notice', 'teams', 'invite', 'connect', 'gateway'] as const;
export const SetupStep = z.object({ id: z.enum(SETUP_STEPS), done: z.boolean(), doneAt: EpochMs.nullable(), optional: z.boolean(), warn: z.boolean() });

/** Home counts are unknown while aggregates are pending or unavailable, never fabricated zeroes. */
export const HomeCliSplitCard = CliSplitCard.extend({
  sessions: Count.nullable(), authModes: CliSplitCard.shape.authModes.nullable(),
  viaGateway: CliSplitCard.shape.viaGateway.extend({ calls: Count.nullable() }),
  footer: CardFooter.extend({ basis: Count.nullable() }),
});
export type HomeCliSplitCard = z.infer<typeof HomeCliSplitCard>;
export const HomeApiSplitCard = ApiSplitCard.extend({
  sessions: Count.nullable(), latency: ApiSplitCard.shape.latency.extend({ n: Count.nullable() }),
  footer: CardFooter.extend({ basis: Count.nullable() }),
});
export type HomeApiSplitCard = z.infer<typeof HomeApiSplitCard>;

export const HomeMemberUsageRow = MemberUsageRow.extend({ prompts: Count.nullable(), sessions: Count.nullable(), byTool: MemberUsageRow.shape.byTool.nullable(), reason: MissingReason.optional() });
export type HomeMemberUsageRow = z.infer<typeof HomeMemberUsageRow>;

/** GET /api/v1/home/summary — non-chart blocks for the requested scope. */
export const HomeSummaryResponse = z.object({
  applied: AppliedFilter,
  /** "기준 시각" printed in the page head and the non-chart card footers. */
  asOf: EpochMs,
  layout: z.enum(HOME_LAYOUTS),
  teamFilterReset: z.literal('small_group').optional(),
  /** No connection and no record ever → only the 'connect your tool' empty state (+ access work). */
  notConnectedYet: z.boolean(),
  cli: HomeCliSplitCard.nullable(),
  api: HomeApiSplitCard.nullable(),
  kpis: z.array(KpiTile),
  accessWork: AccessWorkCard.nullable(),
  recent: z.array(RecentConversation).nullable(),
  /** All of my connections (healthy ones too), problems first; `attention` = status other than ok. */
  myConnections: z.object({ items: z.array(ConnectionAlert), ok: Count, attention: Count }).nullable(),
  members: z.object({ rows: z.array(HomeMemberUsageRow), smallGroup: z.boolean(), reason: MissingReason.optional(), excludedPeople: Count.nullable(), used: Count.nullable(), former: Count.nullable(), footer: CardFooter.extend({ basis: Count.nullable() }) }).nullable(),
  teams: TeamsCompareResponse.pick({ rows: true, small: true, footer: true }).extend({ activePeople: Count.nullable().optional() }).nullable(),
  ratio: z.object({ tokens: SplitRatio.nullable(), counts: SplitRatio.nullable(), sessions: Count.nullable(), reason: MissingReason.optional() }).nullable(),
  /** Teammates' connections needing attention (problem first, then oldest last-seen), at most 6 rows. */
  teamConnections: z.object({ rows: z.array(ConnectionAlert).max(6), teammates: Count, peopleWithIssues: Count, connectionsWithIssues: Count }).nullable(),
  /** Team scope: "사람이 적은 조건에서는 보여 주지 않아요" applies to some cards. */
  smallGroupNotice: z.boolean(),
  anomalies: z.array(Anomaly).nullable(),
  anomalyReason: MissingReason.optional(),
  ingest: IngestSummary.nullable(),
  setup: z.array(SetupStep).nullable(),
  audit: z
    .object({
      contentViews: z.object({ ok: Count, denied: Count }),
      latest: z.array(AuditEntry).max(8),
      /** Last automatic integrity check ("이상 없음 · n건 · 기준 날짜" / "끊긴 곳: #seq"). */
      integrity: z.object({ status: z.enum(['ok', 'chain_broken', 'anchor_mismatch']), count: Count, anchorDate: Ymd.nullable(), brokenAtSeq: z.int().positive().nullable(), checkedAt: EpochMs }).nullable(),
    })
    .nullable(),
  ops: z
    .object({
      daily: z.array(z.object({ date: Ymd, received: Count, duplicates: Count, dropped: Count, stored: Count })),
      connections: z.array(ConnectionAlert).max(8),
      unknownEvents: z.array(z.object({ name: z.string(), count: Count })),
      errorRate: Comparison,
    })
    .nullable(),
});
export type HomeSummaryResponse = z.infer<typeof HomeSummaryResponse>;

/** A home chart card: chart + summary facts + footer. */
export const HomeChartResponse = z.object({
  applied: AppliedFilter,
  state: z.enum(['ok', 'empty', 'small_group', 'missing']),
  reason: MissingReason.optional(),
  teamFilterReset: z.literal('small_group').optional(),
  chart: z.union([TimeChart, GroupChart, HeatChart]).nullable(),
  summary: SummaryFacts,
  footer: CardFooter.extend({ basis: Count.nullable() }),
});
export type HomeChartResponse = z.infer<typeof HomeChartResponse>;

/** GET /api/v1/home/daily — "날마다 얼마나 썼나요?" stacked by method, previous period dashed (default on). */
export const HomeDailyQuery = filterQuery({ metric: z.enum(['prompts', 'tokens', 'sessions']).default('prompts'), prev: z.enum(['0', '1']).default('1') });
/** GET /api/v1/home/models — "어떤 AI 모델을 썼나요?" donut (5 slices + other). */
export const HomeModelsQuery = filterQuery({ metric: z.enum(['tokens', 'requests']).default('tokens') });
/** GET /api/v1/home/heat — weekday × hour prompts; hidden under 3 people (own records exempt). */
export const HomeHeatQuery = filterQuery({});
/** GET /api/v1/home/flow — usage by tool stacked area (scope all). */
export const HomeFlowQuery = filterQuery({});

export const homeSchemas = {
  HomeQuery,
  HomeSummaryResponse,
  HomeChartResponse,
  HomeDailyQuery,
  HomeModelsQuery,
  HomeHeatQuery,
  HomeFlowQuery,
} as const;
